import { useEffect, useRef } from 'react'

interface DigitalOverlayProps {
  theme?: string;
  colors?: {
    primary?: string;
    secondary?: string;
  } | null;
}

const DigitalOverlay: React.FC<DigitalOverlayProps> = ({ theme = 'dark', colors = null }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number
    let digits: Digit[] = []

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()

    // Extract RGB from hex color
    const hexToRgb = (hex: string) => {
      const cleanHex = hex.replace('#', '')
      return {
        r: parseInt(cleanHex.substring(0, 2), 16),
        g: parseInt(cleanHex.substring(2, 4), 16),
        b: parseInt(cleanHex.substring(4, 6), 16)
      }
    }

    // Get theme-aware colors (read dynamically each frame)
    const getDigitColors = () => {
      // Read header accent and secondary colors from CSS variables
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF'
      const secondary = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-secondary')
        .trim() || '#10b981'
      
      const primary = hexToRgb(accent)
      const secondaryRgb = hexToRgb(secondary)
      
      // Create variations of the theme colors for digits
      return [
        `rgba(${primary.r}, ${primary.g}, ${primary.b},`,
        `rgba(${Math.min(255, primary.r + 36)}, ${Math.min(255, primary.g + 30)}, ${Math.min(255, primary.b + 20)},`,
        `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b},`
      ]
    }

    class Digit {
      x: number
      y: number
      value: number
      speed: number
      opacity: number
      size: number
      color: string

      constructor() {
        this.x = 0
        this.y = 0
        this.value = 0
        this.speed = 0
        this.opacity = 0
        this.size = 0
        this.color = ''
        this.reset()
      }

      reset() {
        this.x = Math.random() * canvas.width
        this.y = -20
        this.value = Math.floor(Math.random() * 10)
        this.speed = Math.random() * 2 + 1
        this.opacity = Math.random() * 0.5 + 0.3
        this.size = Math.random() * 20 + 15
        
        const digitColors = getDigitColors()
        this.color = digitColors[Math.floor(Math.random() * digitColors.length)]
      }

      update() {
        this.y += this.speed
        
        if (this.y > canvas.height + 20) {
          this.reset()
        }
        
        // Randomly change digit
        if (Math.random() < 0.02) {
          this.value = Math.floor(Math.random() * 10)
        }
      }

      draw() {
        ctx.font = `${this.size}px monospace`
        ctx.fillStyle = `${this.color} ${this.opacity})`
        ctx.fillText(this.value.toString(), this.x, this.y)
        
        // Glow effect
        ctx.shadowBlur = 10
        ctx.shadowColor = `${this.color} ${this.opacity})`
        ctx.fillText(this.value.toString(), this.x, this.y)
        ctx.shadowBlur = 0
      }
    }

    const createDigits = () => {
      digits = []
      const digitCount = 80
      for (let i = 0; i < digitCount; i++) {
        const digit = new Digit()
        digit.y = Math.random() * canvas.height
        digits.push(digit)
      }
    }

    createDigits()

    const animate = () => {
      // Theme-aware background fade
      const isLightMode = document.documentElement.getAttribute('data-theme') === 'light'
      if (isLightMode) {
        ctx.fillStyle = 'rgba(248, 250, 252, 0.1)'
      } else {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      digits.forEach(digit => {
        digit.update()
        digit.draw()
      })

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    const handleResize = () => {
      resizeCanvas()
      createDigits()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [theme, colors])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1
      }}
    />
  )
}

export default DigitalOverlay
